import { useState } from "react";
import {
	Copy,
	Download,
	Eye,
	MoreHorizontal,
	Printer,
	Share2,
	Undo2,
} from "lucide-react";
import { FileViewerModal } from "@components/file-viewer";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../../../../components/ui/table";
import { TooltipWrapper } from "../../../../components/ui/tooltip-wrapper";
import { cn } from "../../../../lib/utils";
import { formatCurrency, formatDate } from "../../components/table-columns";
import {
	MenuItemData,
	SharedMenuItem,
} from "../../components/shared-menu-item.component";
import {
	ReminderFileRef,
	usePaymentReminderPdf,
} from "../hooks/usePaymentReminderPdf";
import {
	PaymentReminderHistoryRow,
	ReminderHistorySortColumn,
	ReminderHistoryStatus,
} from "../payment-reminder.types";
import { formatReminderDate } from "../utils/reminder-placeholders";
import { OPEN_EPSILON } from "../utils/payment-reminder-document";
import { getReminderStageLabel } from "@/lib/utils/reminder-stage-label";
import { useReminderShare } from "../hooks/useReminderShare";
import { SortableTableHead } from "./sortable-table-head.component";
import { VoidReminderDialog } from "./void-reminder-dialog.component";

interface ReminderHistoryTableProps {
	rows: PaymentReminderHistoryRow[];
	onChanged: () => void;
	showInvoice?: boolean;
	sorting?: { sortBy: ReminderHistorySortColumn; sortDesc: boolean };
	onSort?: (column: ReminderHistorySortColumn) => void;
	highlightedReminderIds?: number[];
}

const HEAD_CLASS = "whitespace-nowrap px-2 py-3";
const CELL_CLASS = "px-2 py-1 text-sm";
const MENU_ICON_CLASS = "h-4 w-4 flex-shrink-0";
const NOT_CONNECTED_HINT = "Kunde ist nicht mit dentCONNECT verbunden";
const STATUS_LABELS: Record<ReminderHistoryStatus, string> = {
	open: "Offen",
	paid: "Gezahlt",
	canceled: "Storniert",
	voided: "Zurückgenommen",
};
const HISTORY_STATUSES: ReminderHistoryStatus[] = [
	"open",
	"paid",
	"canceled",
	"voided",
];
const toHistoryStatus = (status: string): ReminderHistoryStatus =>
	HISTORY_STATUSES.find((entry) => entry === status) ?? "open";
const STATUS_VARIANTS: Record<
	ReminderHistoryStatus,
	"default" | "secondary" | "outline" | "destructive"
> = {
	open: "outline",
	paid: "default",
	canceled: "secondary",
	voided: "secondary",
};

const toFileRef = (row: PaymentReminderHistoryRow): ReminderFileRef | null =>
	row.path_name
		? {
				reminderId: row.reminder_id,
				stage: row.reminder_stage,
				templateType: row.template_type,
				docNumber: row.doc_number,
				pathName: row.path_name,
			}
		: null;

const feeStatusLabel = (row: PaymentReminderHistoryRow) => {
	if (row.fee_canceled) return "storniert";
	return row.fee_open_amount > OPEN_EPSILON ? "offen" : "bezahlt";
};

const DeliveryIcon: React.FC<{
	at: string | null;
	label: string;
	icon: React.ReactNode;
	unavailableReason?: string;
}> = ({ at, label, icon, unavailableReason }) => (
	<TooltipWrapper
		title={
			at
				? `${label} am ${formatReminderDate(at)}`
				: unavailableReason ?? `Nicht ${label.toLowerCase()}`
		}
	>
		<span className={at ? "text-green-600" : "text-gray-300"}>{icon}</span>
	</TooltipWrapper>
);

export const ReminderHistoryTable: React.FC<ReminderHistoryTableProps> = ({
	rows,
	onChanged,
	showInvoice = true,
	sorting,
	onSort,
	highlightedReminderIds = [],
}) => {
	const { toggleReminderShare, shareEmailDialog } =
		useReminderShare(onChanged);
	const { printReminder, downloadReminder } = usePaymentReminderPdf();
	const [menuReminderId, setMenuReminderId] = useState<number | null>(null);
	const [viewerFileId, setViewerFileId] = useState<string | null>(null);
	const [voidTarget, setVoidTarget] =
		useState<PaymentReminderHistoryRow | null>(null);
	const handlePrint = async (
		row: PaymentReminderHistoryRow,
		asCopy: boolean
	) => {
		const file = toFileRef(row);
		if (!file) return;
		await printReminder(file, { asCopy });
		onChanged();
	};

	const handleDownload = async (row: PaymentReminderHistoryRow) => {
		const file = toFileRef(row);
		if (!file) return;
		await downloadReminder(file);
		onChanged();
	};

	const sortableHead = (
		column: ReminderHistorySortColumn,
		label: string,
		alignRight = false
	) => (
		<SortableTableHead
			sorted={
				sorting?.sortBy === column
					? sorting.sortDesc
						? "desc"
						: "asc"
					: false
			}
			onToggle={onSort && (() => onSort(column))}
			alignRight={alignRight}
		>
			{label}
		</SortableTableHead>
	);

	const menuItems = (row: PaymentReminderHistoryRow): MenuItemData[] => {
		const voided = row.voided_at !== null;
		const hasFile = row.path_name !== null && !voided;
		const shareBlocked = !row.shared_at && !row.is_connected;
		return [
			{
				icon: <Eye className={MENU_ICON_CLASS} />,
				label: "Dokument öffnen",
				onClick: () => setViewerFileId(row.file_id),
				disabled: !row.file_id || !row.path_name,
				testId: "reminder-action-open",
			},
			{
				icon: <Printer className={MENU_ICON_CLASS} />,
				label: "Dokument drucken",
				onClick: () => handlePrint(row, false),
				disabled: !hasFile,
				testId: "reminder-action-print",
			},
			{
				icon: <Download className={MENU_ICON_CLASS} />,
				label: "Dokument herunterladen",
				onClick: () => handleDownload(row),
				disabled: !hasFile,
				testId: "reminder-action-download",
			},
			{
				icon: <Copy className={MENU_ICON_CLASS} />,
				label: "Als Kopie drucken",
				onClick: () => handlePrint(row, true),
				disabled: !hasFile,
				testId: "reminder-action-print-copy",
			},
			{
				icon: <Share2 className={MENU_ICON_CLASS} />,
				label: row.shared_at
					? "Teilen aufheben"
					: row.unshared_at
						? "Erneut teilen"
						: "Dokument teilen",
				onClick: () => toggleReminderShare(row),
				disabled: !hasFile || shareBlocked,
				tooltip: hasFile && shareBlocked ? NOT_CONNECTED_HINT : undefined,
				testId: "reminder-action-share",
			},
		];
	};

	return (
		<>
			<Table data-testid="reminder-history-table">
				<TableHeader>
					<TableRow>
						{sortableHead("sent_at", "Mahndatum")}
						{showInvoice &&
							sortableHead("doc_number", "Rechnungsnummer")}
						{showInvoice &&
							sortableHead("recipient_name", "Empfänger")}
						{sortableHead("reminder_stage", "Mahnstufe")}
						{sortableHead("fee_amount", "Gebühr", true)}
						<TableHead className={HEAD_CLASS}>
							Gebührenrechnung
						</TableHead>
						<TableHead className={HEAD_CLASS}>Zustellung</TableHead>
						<TableHead className={HEAD_CLASS}>Status</TableHead>
						<TableHead className={cn(HEAD_CLASS, "w-10")} />
					</TableRow>
				</TableHeader>
				<TableBody>
					{rows.map((row) => {
						const voided = row.voided_at !== null;
						const status = toHistoryStatus(row.status);
						const testIdSuffix = `${row.doc_number}-${row.reminder_stage}`;
						return (
							<TableRow
								key={row.reminder_id}
								className={cn(
									voided && "text-gray-400",
									highlightedReminderIds.includes(
										row.reminder_id
									) && "bg-green-50"
								)}
								data-testid={`reminder-history-row-${testIdSuffix}`}
								onContextMenu={(event) => {
									event.preventDefault();
									setMenuReminderId(row.reminder_id);
								}}
							>
								<TableCell className={CELL_CLASS}>
									{formatDate(row.sent_at)}
								</TableCell>
								{showInvoice && (
									<TableCell className={CELL_CLASS}>
										{row.doc_number}
									</TableCell>
								)}
								{showInvoice && (
									<TableCell className={CELL_CLASS}>
										{row.recipient_name}
									</TableCell>
								)}
								<TableCell className={CELL_CLASS}>
									<span className="flex flex-wrap items-center gap-1">
										<span
											className={cn(
												voided && "line-through"
											)}
										>
											{getReminderStageLabel(
												row.reminder_stage
											)}
										</span>
										{row.source === "dlxp_migration" && (
											<Badge variant="outline">
												DLXP
											</Badge>
										)}
									</span>
									{voided && row.void_reason && (
										<span className="block text-xs">
											{row.void_reason}
										</span>
									)}
								</TableCell>
								<TableCell
									className={cn(
										CELL_CLASS,
										"text-right font-mono"
									)}
								>
									{row.fee_amount > 0
										? formatCurrency(row.fee_amount)
										: "–"}
								</TableCell>
								<TableCell className={CELL_CLASS}>
									{row.fee_invoice_id ? (
										<span className="whitespace-nowrap">
											{row.fee_doc_number}
											<span className="text-gray-500">
												{" · "}
												{feeStatusLabel(row)}
											</span>
										</span>
									) : (
										<span className="text-gray-400">–</span>
									)}
								</TableCell>
								<TableCell className={CELL_CLASS}>
									<span className="flex items-center gap-2">
										<DeliveryIcon
											at={row.printed_at}
											label="Gedruckt"
											icon={
												<Printer className="h-4 w-4" />
											}
										/>
										<DeliveryIcon
											at={row.downloaded_at}
											label="Heruntergeladen"
											icon={
												<Download className="h-4 w-4" />
											}
										/>
										<DeliveryIcon
											at={row.shared_at}
											label="Geteilt"
											icon={
												<Share2 className="h-4 w-4" />
											}
											unavailableReason={
												row.unshared_at
													? `Teilen am ${formatReminderDate(row.unshared_at)} aufgehoben`
													: row.is_connected
														? undefined
														: NOT_CONNECTED_HINT
											}
										/>
									</span>
								</TableCell>
								<TableCell className={CELL_CLASS}>
									<Badge
										variant={STATUS_VARIANTS[status]}
										data-testid={`reminder-status-${testIdSuffix}`}
									>
										{STATUS_LABELS[status]}
									</Badge>
								</TableCell>
								<TableCell className={CELL_CLASS}>
									<DropdownMenu
										open={
											menuReminderId === row.reminder_id
										}
										onOpenChange={(open) =>
											setMenuReminderId(
												open ? row.reminder_id : null
											)
										}
									>
										<DropdownMenuTrigger asChild>
											<Button
												variant="ghost"
												size="sm"
												className="h-6 w-6 p-0"
												aria-label={`Aktionen für Mahnung ${row.doc_number}`}
												data-testid={`reminder-actions-${testIdSuffix}`}
											>
												<MoreHorizontal />
											</Button>
										</DropdownMenuTrigger>
										<DropdownMenuContent align="end">
											{menuItems(row).map((item) => (
												<SharedMenuItem
													key={item.testId}
													item={item}
												/>
											))}
											<DropdownMenuSeparator />
											<SharedMenuItem
												item={{
													icon: (
														<Undo2
															className={
																MENU_ICON_CLASS
															}
														/>
													),
													label: `${getReminderStageLabel(
														row.reminder_stage
													)} zurücknehmen...`,
													onClick: () =>
														setVoidTarget(row),
													disabled: !row.is_latest,
													className:
														"text-red-600 focus:text-red-600",
													testId: "reminder-action-void",
												}}
											/>
										</DropdownMenuContent>
									</DropdownMenu>
								</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>

			{viewerFileId && (
				<FileViewerModal
					fileIds={viewerFileId}
					isOpen
					onClose={() => setViewerFileId(null)}
				/>
			)}
			<VoidReminderDialog
				reminder={voidTarget}
				onClose={() => setVoidTarget(null)}
				onVoided={onChanged}
			/>
			{shareEmailDialog}
		</>
	);
};
